import { NOTIFICATION_CENTER_TAB_IDS } from "./notificationCenterConfig";
import {
  NOTIFICATION_CENTER_DEADLINES_SCOPE,
  NOTIFICATION_CENTER_VISIBILITY,
} from "../utils/notificationCenterFilterConstants";

/** @typedef {'select'} NotificationCenterFilterType */

/**
 * @typedef {Object} NotificationCenterFilterOptionDef
 * @property {string} value
 * @property {string} labelKey
 */

/**
 * @typedef {Object} NotificationCenterFilterDef
 * @property {string} id
 * @property {NotificationCenterFilterType} type
 * @property {string} reduxKey
 * @property {string} placeholderKey
 * @property {string} ariaLabelKey
 * @property {NotificationCenterFilterOptionDef[]} options
 * @property {'labeled' | null} [variant]
 * @property {string} [labelPrefixKey]
 */

export const NOTIFICATION_CENTER_FILTER_IDS = Object.freeze({
  VISIBILITY: "visibility",
  DEADLINES_SCOPE: "deadlinesScope",
});

const VISIBILITY_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.VISIBILITY,
  type: "select",
  reduxKey: "visibilityFilter",
  placeholderKey: "filters.visibility.mine",
  ariaLabelKey: "filters.visibility.mine",
  options: [
    {
      value: NOTIFICATION_CENTER_VISIBILITY.MINE,
      labelKey: "filters.visibility.mine",
    },
    {
      value: NOTIFICATION_CENTER_VISIBILITY.ALL,
      labelKey: "filters.visibility.all",
    },
  ],
});

const DEADLINES_SCOPE_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.DEADLINES_SCOPE,
  type: "select",
  reduxKey: "deadlinesScopeFilter",
  variant: "labeled",
  labelPrefixKey: "filters.deadlinesPrefix",
  placeholderKey: "filters.deadlines.next3Days",
  ariaLabelKey: "filters.deadlines.next3Days",
  options: [
    {
      value: NOTIFICATION_CENTER_DEADLINES_SCOPE.NEXT_3_DAYS,
      labelKey: "filters.deadlines.next3Days",
    },
    {
      value: NOTIFICATION_CENTER_DEADLINES_SCOPE.FORTHCOMING,
      labelKey: "filters.deadlines.forthcoming",
    },
    {
      value: NOTIFICATION_CENTER_DEADLINES_SCOPE.ALL,
      labelKey: "filters.deadlines.all",
    },
    {
      value: NOTIFICATION_CENTER_DEADLINES_SCOPE.MISSED,
      labelKey: "filters.deadlines.missed",
    },
  ],
});

/** @type {Record<string, NotificationCenterFilterDef>} */
export const NOTIFICATION_CENTER_FILTER_DEFINITIONS = Object.freeze({
  [NOTIFICATION_CENTER_FILTER_IDS.VISIBILITY]: VISIBILITY_FILTER,
  [NOTIFICATION_CENTER_FILTER_IDS.DEADLINES_SCOPE]: DEADLINES_SCOPE_FILTER,
});

export const NOTIFICATION_CENTER_FILTER_DEFAULTS = Object.freeze({
  visibilityFilter: NOTIFICATION_CENTER_VISIBILITY.MINE,
  deadlinesScopeFilter: NOTIFICATION_CENTER_DEADLINES_SCOPE.NEXT_3_DAYS,
});

/**
 * @param {Record<string, unknown>} [raw]
 * @returns {{ visibilityFilter: string, deadlinesScopeFilter: string }}
 */
export function normalizeNotificationCenterFilterState(raw) {
  const normalized = { ...NOTIFICATION_CENTER_FILTER_DEFAULTS };

  for (const def of Object.values(NOTIFICATION_CENTER_FILTER_DEFINITIONS)) {
    const value = raw?.[def.reduxKey];
    const allowed = def.options.map((option) => option.value);
    if (allowed.includes(value)) {
      normalized[def.reduxKey] = value;
    }
  }

  return normalized;
}

/**
 * @param {Record<string, unknown>} state
 */
export function pickNotificationCenterPersistedFilters(state) {
  return normalizeNotificationCenterFilterState(state);
}

/**
 * @param {string | null | undefined} tabId
 */
export function getDefaultFiltersForTab(tabId) {
  const defs = getNotificationCenterFiltersForTab(tabId);
  const defaults = {};

  for (const def of defs) {
    defaults[def.reduxKey] = NOTIFICATION_CENTER_FILTER_DEFAULTS[def.reduxKey];
  }

  return defaults;
}

/**
 * @param {string | null | undefined} tabId
 * @param {Record<string, unknown>} [raw]
 */
export function normalizeNotificationCenterFilterStateForTab(tabId, raw) {
  const normalized = getDefaultFiltersForTab(tabId);
  const global = normalizeNotificationCenterFilterState(raw);

  for (const def of getNotificationCenterFiltersForTab(tabId)) {
    if (global[def.reduxKey] !== undefined) {
      normalized[def.reduxKey] = global[def.reduxKey];
    }
  }

  return normalized;
}

const DEFAULT_TAB_FILTER_IDS = [NOTIFICATION_CENTER_FILTER_IDS.VISIBILITY];

/**
 * Filter ids shown per tab. Extend this map when a tab needs custom filters.
 * @type {Record<string, string[]>}
 */
export const NOTIFICATION_CENTER_TAB_FILTER_IDS = Object.freeze({
  [NOTIFICATION_CENTER_TAB_IDS.REPORTING]: DEFAULT_TAB_FILTER_IDS,
  [NOTIFICATION_CENTER_TAB_IDS.MESSAGES]: DEFAULT_TAB_FILTER_IDS,
  [NOTIFICATION_CENTER_TAB_IDS.CASES]: DEFAULT_TAB_FILTER_IDS,
  [NOTIFICATION_CENTER_TAB_IDS.CHARGE_INVOICES]: DEFAULT_TAB_FILTER_IDS,
  [NOTIFICATION_CENTER_TAB_IDS.PRODUCT]: DEFAULT_TAB_FILTER_IDS,
  [NOTIFICATION_CENTER_TAB_IDS.SETTINGS]: DEFAULT_TAB_FILTER_IDS,
  [NOTIFICATION_CENTER_TAB_IDS.DEADLINES]: [
    NOTIFICATION_CENTER_FILTER_IDS.VISIBILITY,
    NOTIFICATION_CENTER_FILTER_IDS.DEADLINES_SCOPE,
  ],
});

/**
 * @param {string | null | undefined} tabId
 * @returns {NotificationCenterFilterDef[]}
 */
export function getNotificationCenterFiltersForTab(tabId) {
  const filterIds = NOTIFICATION_CENTER_TAB_FILTER_IDS[tabId] ?? DEFAULT_TAB_FILTER_IDS;
  return filterIds
    .map((id) => NOTIFICATION_CENTER_FILTER_DEFINITIONS[id])
    .filter(Boolean);
}

/**
 * @param {Record<string, unknown>} notificationCenterState
 * @param {string | null | undefined} tabId
 */
export function getNotificationCenterFilterValues(notificationCenterState, tabId) {
  return normalizeNotificationCenterFilterStateForTab(tabId, notificationCenterState);
}
